import { Component, h, Host, Prop } from '@stencil/core';

import { renderResponsiveImages } from '../../helpers/helpers';
import {
  CssClassMap,
  ResponsiveImages,
  Store,
  Stores,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-market-module',
  styleUrl: 'cms-market-module.scss',
  shadow: true,
})
export class CmsMarketModule {
  private storesData: Stores;

  private currentStoreId: string;

  private store: Store;

  private responsiveMarketImages: ResponsiveImages | string;

  private marketImageSizes = `
(min-width: 992px) and (max-width: 1199px) and (min-resolution: 288dpi) 30vw,
(min-width: 992px) and (max-width: 1199px) and (min-resolution: 192dpi) 30vw,
(min-width: 992px) and (max-width: 1199px) and (min-resolution: 96dpi) 60vw,
(min-width: 1200px)  700px,
 100vw`;

  /**
   * set the responsive Market image for Breakpoints 320, 480, 768,992,1200
   */
  @Prop() marketImage: string;

  /**
   * get all Market Information from alle-mearkte.json
   */
  @Prop() allMarketDataUrl: string;
  /**
   * get the headline of the Element like Willkommen in Deinem Dauertiefpreisgebiet.
   */
  @Prop({ reflect: true }) moduleHeadline: string;
  /**
   * get the Button label Text like ZU allen Marktinformationen
   */
  @Prop() marketInfoButtonLabel: string;
  /**
   * get the contact information  label text like kontaktformular
   */
  @Prop() contactLabel: string;

  /**
   * get the url to Kontaktformular
   */
  @Prop() contactFormUrl: string;

  /**
   * get the open Times section label like öffnungszeiten
   */
  @Prop() openTimesLabel: string;
  /**
   * get the market closed information label text like geschlossen
   */
  @Prop() closedLabel: string;
  /**
   * get the search market section label like nicht der richtige markt
   */
  @Prop() searchMarketLabel: string;
  /**
   * get the market search button label text like markt suchen
   */
  @Prop() searchMarketButtonLabel: string;

  /**
   * get market overview url
   */
  @Prop() searchMarketUrl: string;

  /**
   * get the google Maps Link Label like route planen
   */
  @Prop() googleMapsLabel: string;

  private iconWrapperCssClass(): CssClassMap {
    return {
      ['address-icon-wrapper']: true,
    };
  }

  private renderMarketImage() {
    return (
      <div class={'grid-item market-image-wrapper'}>
        {this.responsiveMarketImages != null
          ? renderResponsiveImages(
              this.responsiveMarketImages as ResponsiveImages,
              this.marketImageSizes,
              'market image',
              'market-image',
            )
          : ''}
      </div>
    );
  }

  private renderMarketInfos() {
    return (
      <div class={'market-info-wrapper grid-item'}>
        <div class={'address-box flex-item'}>{this.renderAddressContent()}</div>
        <div class={'open-times-box flex-item'}>
          {this.renderOpenTimesContent()}
        </div>{' '}
        {this.storesData.stores.length > 1 ? (
          <div class={'search-box flex-item'}>
            {this.renderMarketSearchContent()}
          </div>
        ) : null}
      </div>
    );
  }

  private renderAddressContent() {
    return (
      <div class={'address-content'}>
        <div class={'icon-wrapper'}>
          <cms-icon size='md' icon='locator'></cms-icon>
        </div>
        <div class={'market-address-wrapper'}>
          <div
            class='store-address'
            itemProp='address'
            itemScope
            itemType='http://schema.org/PostalAddress'
          >
            <label class='market-name label' itemProp='addressLocality'>
              {this.store.name !== undefined ? this.store.name : ''}
            </label>
          </div>
          <div class={'market-street'}>
            {this.store.street !== undefined ? this.store.street : ''}
          </div>
          <div class={'market-city-wrapper'}>
            <span class={'zipCode'}>
              {' '}
              {this.store.postalcode !== undefined ? this.store.postalcode : ''}
            </span>
            <span class={'market-city'}>
              {' '}
              {this.store.city !== undefined ? this.store.city : ''}
            </span>
          </div>

          <div class={'market-phone'}>
            <span class={this.iconWrapperCssClass()}>
              <cms-icon size='sm' icon='phone'></cms-icon>
            </span>
            <a
              class='contact-phone-link market-info-link'
              itemProp='telephone'
              href={`tel:${this.store.phoneInternational}`}
            >
              {this.store.phone !== undefined ? this.store.phone : ''}
            </a>
          </div>
          <div class={'market-map'}>
            <span class={this.iconWrapperCssClass()}>
              <cms-icon size='sm' icon='locator'></cms-icon>
            </span>
            <a
              title={'googlemaps-link'}
              aria-label='googlemaps-link'
              href={this.buildMapsUrl()}
              class={'sore-maps-link market-info-link'}
              target='_blank'
              rel='noopener'
            >
              {this.googleMapsLabel}
            </a>
          </div>
          <div class={'market-contact'}>
            <span class={this.iconWrapperCssClass()}>
              <cms-icon size='sm' icon='mail'></cms-icon>
            </span>
            <a
              title={this.contactLabel !== undefined ? this.contactLabel : ''}
              rel='noopener'
              href={
                this.contactFormUrl !== undefined ? this.contactFormUrl : ''
              }
              class='contact-form-link market-info-link'
            >
              {this.contactLabel !== undefined ? this.contactLabel : ''}
            </a>
          </div>
        </div>
      </div>
    );
  }

  private buildMapsUrl() {
    return encodeURI(
      this.storesData.navigationUrl
        .replace('{{name}}', `${this.store.name}`)
        .replace('{{street}}', `${this.store.street}`),
    );
  }
  private renderOpenTimesContent() {
    return (
      <div class={'open-times-content'}>
        <div class={'icon-wrapper'}>
          <cms-icon size='md' icon='clock'></cms-icon>
        </div>
        <div class={'open-times-wrapper'}>
          <div class={'label open-times-label'}>
            {this.openTimesLabel !== undefined ? this.openTimesLabel : ''}
          </div>

          <div class={'market-open-times'}>{this.renderMapOpenTimes()}</div>
          {this.store.specialTimes !== undefined
            ? this.renderMapSpecialTimes()
            : null}

          <div
            class={'market-closed-label'}
            innerHTML={this.closedLabel !== undefined ? this.closedLabel : ''}
          ></div>
        </div>
      </div>
    );
  }

  private renderMapOpenTimes() {
    if (this.store) {
      const { regularTimes } = this.store;
      return regularTimes.map((openTime) => {
        return (
          <div key={openTime.day} class={'store-open-time'}>
            <span class={'open-day'}>
              {openTime.day !== undefined ? openTime.day : ''}
            </span>
            <span class={'open-time'}>
              {openTime.from !== undefined
                ? `${openTime.from} - ${openTime.until} ${this.storesData.labels.time}`
                : ''}
            </span>
          </div>
        );
      });
    }
  }

  private renderMapSpecialTimes() {
    if (this.store) {
      const { specialTimes } = this.store;
      return specialTimes.map((openSpecialTime) => {
        return (
          <div key={openSpecialTime.date} class={'Store-special-time'}>
            <div class={'open-date'}>
              <span class={'special-date'}>
                {openSpecialTime.date !== undefined ? openSpecialTime.date : ''}
              </span>
              {openSpecialTime.isOpen ? (
                <span class={'from-time'}>
                  {openSpecialTime.from} - {openSpecialTime.until}{' '}
                  {this.storesData.labels.time}
                </span>
              ) : (
                <div
                  class={'market-closed-label'}
                  innerHTML={
                    this.storesData.labels.closed !== undefined
                      ? encodeURI(this.storesData.labels.closed)
                      : ''
                  }
                ></div>
              )}
            </div>
          </div>
        );
      });
    }
  }

  private renderMarketSearchContent() {
    return (
      <div class={'market-search-content'}>
        <div class={'search-label label'}>
          <cms-icon size='md' icon='store'></cms-icon>
          <span style={{ marginLeft: '1rem' }}>
            {this.searchMarketLabel !== undefined ? this.searchMarketLabel : ''}
          </span>
        </div>

        <div class={'search-button-wrapper'}>
          <cms-button
            onCmsButtonClick={() => {
              this.openUrl(this.searchMarketUrl);
            }}
            type='button'
            size='button-sm'
            theme='ghost-dark'
            label={this.searchMarketButtonLabel}
          ></cms-button>
        </div>
      </div>
    );
  }

  private renderMarketInfoButton() {
    return (
      <div class={'market-info-button-wrapper'}>
        <cms-button
          onCmsButtonClick={() => this.openUrl(this.store.url)}
          type='button'
          size='button-sm'
          theme='ghost-light'
          label={this.marketInfoButtonLabel}
        ></cms-button>
      </div>
    );
  }

  private async fetchData(apiUrl: RequestInfo | URL) {
    if (apiUrl) {
      try {
        const dataResponse = await fetch(apiUrl);
        return await dataResponse.json();
      } catch (error) {
        console.info('error', error);
        throw error;
      }
    }
  }

  private async setCurrentStoreID(): Promise<string> {
    //@ts-ignore
    const hbDataLayer = window.hbDataLayer;
    if (hbDataLayer) {
      return await hbDataLayer['site.market'];
    }
  }

  private async filterStoreId(stores: Store[]) {
    return stores.find((store: Store) => store.id == this.currentStoreId);
  }

  async componentWillLoad() {
    this.currentStoreId = await this.setCurrentStoreID();

    if (this.allMarketDataUrl !== undefined) {
      this.storesData = await this.fetchData(this.allMarketDataUrl);
    }

    if (this.storesData) {
      this.store = await this.filterStoreId(this.storesData.stores);
    }

    if (!this.store) {
      return;
    }
    const { images } = this.store;
    const imagesLength = Object.keys(images).length;
    this.responsiveMarketImages =
      imagesLength > 0
        ? images
        : ((await await JSON.parse(this.marketImage)) as ResponsiveImages);
  }

  private openUrl(url: string) {
    const win = window.open(url, '_self');
    if (win != null) {
      win.focus();
    }
  }

  render() {
    return (
      <Host class={'has-bg'}>
        <div class={'store-module-inner'}>
          <div class={'store-module-headline-wrapper'}>
            <cms-headline
              headlineText={
                this.moduleHeadline != undefined ? this.moduleHeadline : ''
              }
              headlineTag='h2'
              headlineClass='headline'
              headlinePosition='center'
            ></cms-headline>
          </div>
          <div class={'store-module-content'}>
            {this.marketImage !== undefined ? this.renderMarketImage() : null}
            {this.storesData !== undefined ? this.renderMarketInfos() : null}
          </div>
          {this.marketInfoButtonLabel !== undefined
            ? this.renderMarketInfoButton()
            : null}
        </div>
      </Host>
    );
  }
}
